import { stringify as generateQueryString } from 'qs';

import Config from '../config';
import {
  handleErrors,
  handleTimeout,
  parseAsJson,
} from '../utils/requestHelpers';
import fetch from '../utils/fetchService';

import { YES_FLAG, DEFAULT_TRADING_PARTNER } from '../shared/constants';

export const fetchCountryTaxTypes = async ({ companyCode, period, staticOptions }) => await fetch(
  Config.endpoints.TAX_ENTRIES_COUNTRY_TAX_TYPES
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:staticOptions/, staticOptions)
)
  .then(handleErrors({ errorMessage: 'Fetching Country Tax Types data failed' }))
  .then(parseAsJson);

export const fetchTaxEntries = async ({
  companyCode,
  countryTaxType,
  period,
  taxYear,
  bookType,
}) => await fetch(
  Config.endpoints.TAX_ENTRIES
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:taxYear/, taxYear)
    .replace(/:countryTaxType/, countryTaxType)
    .replace(/:bookType/, bookType)
)
  .then(handleErrors({ errorMessage: 'Fetching Tax Entries failed' }))
  .then(parseAsJson);

export const fetchNotifications = async ({
  period,
  connection,
}) => fetch(
  Config.endpoints.FETCH_NOTIFICATIONS
    .replace(/:period/, period)
    .replace(/:connection/, connection)
)
  .then(handleErrors({ errorMessage: 'Fetching Notifications failed' }))
  .then(parseAsJson);

export const fetchTaxEntryData = async taxEntryId => await fetch(
  Config.endpoints.TAX_ENTRY.replace(/:taxEntryId/, taxEntryId)
)
  .then(handleErrors({ errorMessage: 'Fetching Tax Entry failed' }))
  .then(parseAsJson);

export const fetchSubledgerTaxEntries = async ({
  companyCode,
  period,
}) => await fetch(
  Config.endpoints.FETCH_SUBLEDGER_TAX_ENTRIES
    .replace(/:period/, period)
    .replace(/:companyCode/, companyCode)
)
  .then(handleErrors({ errorMessage: 'Fetching Tax Entries failed' }))
  .then(parseAsJson);

export const fetchProposedJournals = async ({
  companyCode,
  period,
  taxYear,
  countryTaxType,
  entryType,
  entryActivity,
  defTaxRate,
  taxRate,
  netAmount,
  grossAmount,
  topsideFlag,
  bookType,
  glAccountOverride,
  account,
  fromAccount,
  allowGross,
}) => await fetch(
  Config.endpoints.FETCH_PROPOSED_JOURNALS
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:queryString/, generateQueryString({
      taxYear,
      countryTaxType,
      entryType,
      entryActivity,
      bookType,
      tradingPartner: DEFAULT_TRADING_PARTNER,
      transferFlag: YES_FLAG,
      topsideFlag,
      glAccountOverride,
      defTaxRate,
      taxRate,
      netAmount,
      grossAmount,
      account,
      fromAccount,
      allowGross,
    }))
)
  .then(handleErrors({ errorMessage: 'Fetching Proposed Journals Failed' }))
  .then(parseAsJson);

export const fetchProposedJournalsForReclass = async ({
  companyCode,
  period,
  taxYear,
  toTaxYear,
  countryTaxType,
  entryType,
  entryActivity,
  netAmount,
  grossAmount,
  netToAmount,
  grossToAmount,
  bookType,
  account,
  toAccount,
  allowGross,
}) => await fetch(
  Config.endpoints.FETCH_PROPOSED_JOURNALS
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:queryString/, generateQueryString({
      taxYear,
      toTaxYear,
      countryTaxType,
      entryType,
      entryActivity,
      netAmount,
      grossAmount,
      netToAmount,
      grossToAmount,
      tradingPartner: DEFAULT_TRADING_PARTNER,
      bookType,
      account,
      toAccount,
      allowGross,
    }))
)
  .then(handleErrors({ errorMessage: 'Fetching Proposed Journals Failed' }))
  .then(parseAsJson);

export const submitTaxEntry = async params => await fetch(
  Config.endpoints.CREATE_TAX_ENTRY,
  {
    method: 'POST',
    body: JSON.stringify(params),
  }
)
  .then(handleErrors({ errorMessage: 'Submitting Tax Entry Failed' }));

export const submitTaxEntries = directEntries => fetch(
  Config.endpoints.CREATE_TAX_ENTRIES,
  {
    method: 'POST',
    body: JSON.stringify(directEntries),
  }
)
  .then(handleErrors({ errorMessage: 'Submitting Tax Entries Failed' }))
  .catch(handleTimeout);

export const fetchSubledgerGlAccounts = async ({
  period,
}) => await fetch(
  Config.endpoints.FETCH_SUBLEDGER_GL_ACCOUNTS
    .replace(/:period/, period)
)
  .then(handleErrors({ errorMessage: 'Fetching Gl Accounts failed' }))
  .then(parseAsJson);

export const fetchTaxEntriesForApproval = async ({
  periodName,
  preparerSSO,
}) => await fetch(
  Config.endpoints.FETCH_TAX_ENTRIES_FOR_APPROVAL
    .replace(/:periodName/, periodName)
    .replace(/:preparerSSO/, preparerSSO)
)
  .then(handleErrors({ errorMessage: 'Fetching Tax Entries For Approval failed' }))
  .then(parseAsJson);

export const approveTaxEntries = async ({ taxEntryIds, reviewComment, lockId }) => await fetch(
  Config.endpoints.APPROVE_TAX_ENTRIES,
  {
    method: 'POST',
    body: JSON.stringify({
      taxEntryIds,
      reviewComment,
      lockId,
    }),
  }
)
  .then(handleErrors({ errorMessage: 'Approving Tax Entries Failed' }))
  .catch(error => {
    console.log('will retry same request on error, in 120s', error); // eslint-disable-line no-console
    return (new Promise(resolve => setTimeout(resolve, 120000)))
      .then(() => fetch(
        Config.endpoints.APPROVE_TAX_ENTRIES,
        {
          method: 'POST',
          body: JSON.stringify({
            taxEntryIds,
            reviewComment,
            lockId,
          }),
        }
      ))
      .then(handleErrors({ errorMessage: 'Approving Tax Entries Failed' }));
  })
  .catch(handleTimeout);

export const declineTaxEntries = async ({ taxEntryIds, reviewComment, lockId }) => await fetch(
  Config.endpoints.DECLINE_TAX_ENTRIES,
  {
    method: 'POST',
    body: JSON.stringify({
      taxEntryIds,
      reviewComment,
      lockId,
    }),
  }
)
  .then(handleErrors({ errorMessage: 'Declining Tax Entries Failed' }))
  .catch(handleTimeout);

export const fetchTaxEntryForApprovalAttachments = async taxEntryId => await fetch(
  Config.endpoints.FETCH_TAX_ENTRY_FOR_APPROVAL_ATTACHMENTS
    .replace(/:taxEntryId/, taxEntryId)
)
  .then(handleErrors({ errorMessage: 'Error Fetching Attachments' }))
  .then(parseAsJson);

export const unlockTaxEntriesForApproval = async lockId => await fetch(
  Config.endpoints.UNLOCK_TAX_ENTRIES_FOR_APPROVAL
    .replace(/:lockId/, lockId),
  {
    method: 'DELETE',
  }
).then(handleErrors({ errorMessage: 'Error unlocking tax entries' }))
  .catch(handleTimeout);

export const fetchAccountActivity = async ({
  periodName,
  companyCode,
  taxYear,
  countryTaxType,
  slAccount,
  accountId,
  bookType,
}) => await fetch(
  Config.endpoints.FETCH_ACCOUNT_ACTIVITY
    .replace(/:periodName/, periodName)
    .replace(/:companyCode/, companyCode)
    .replace(/:taxYear/, taxYear)
    .replace(/:countryTaxType/, countryTaxType)
    .replace(/:slAccount/, slAccount)
    .replace(/:accountId/, accountId)
    .replace(/:bookType/, bookType)
)
  .then(handleErrors({ errorMessage: 'Error Fetching Account Activity' }))
  .then(parseAsJson);

export const fetchReportDataWithTaxYears = async ({
  reportName,
  fromPeriod,
  toPeriod,
  groupType,
  groupItem,
  countryTaxType,
  bookType,
  taxYear,
  reportingCurrency,
  aggregateBy,
  grossFlag,
}) => await fetch(
  Config.endpoints.FETCH_BALANCE_SHEET_MANAGEMENT_REPORT
    .replace(/:reportName/, reportName)
    .replace(/:queryString/, generateQueryString({
      fromPeriod,
      toPeriod,
      groupType,
      groupItem,
      countryTaxType,
      taxYear,
      bookType,
      reportingCurrency,
      aggregateBy,
      grossFlag,
    }))
)
  .then(handleErrors({ errorMessage: 'Error Fetching Report Data' }))
  .then(parseAsJson);

export const fetchReportDataForGroup = async ({
  reportName,
  fromPeriod,
  toPeriod,
  groupType,
  groupItem,
  countryTaxType,
  bookType,
  reportingCurrency,
}) => await fetch(
  Config.endpoints.FETCH_BALANCE_SHEET_MANAGEMENT_REPORT
    .replace(/:reportName/, reportName)
    .replace(/:queryString/, generateQueryString({
      fromPeriod,
      toPeriod,
      groupType,
      groupItem,
      countryTaxType,
      bookType,
      reportingCurrency,
    }))
)
  .then(handleErrors({ errorMessage: 'Error Fetching Report Data' }))
  .then(parseAsJson);

export const fetchSlAccounts = async ({ periodName }) => await fetch(
  Config.endpoints.FETCH_SL_ACCOUNTS_FOR_ACCOUNT_BALANCES
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching SL Accounts failed' }))
  .then(parseAsJson);

export const fetchAccountBalances = async ({ periodName, displayBy }) => await fetch(
  Config.endpoints.FETCH_ACCOUNT_BALANCES
    .replace(/:periodName/, periodName)
    .replace(/:displayBy/, displayBy)
)
  .then(handleErrors({ errorMessage: 'Fetching Account Balances failed' }))
  .then(parseAsJson);

export const fetchDiagnostics = async ({
  period,
  connection,
}) => fetch(
  Config.endpoints.FETCH_BSM_DIAGNOSTICS
    .replace(/:period/, period)
    .replace(/:connection/, connection)
)
  .then(handleErrors({ errorMessage: 'Fetching Diagnostics failed' }))
  .then(parseAsJson);

export const fetchTaxEntriesTree = async ({
  periodName,
  companyCode,
  taxYear,
  countryTaxType,
  forEntry = true,
  forReclass = false,
}) =>
  await fetch(
    Config.endpoints.FETCH_TAX_ENTRIES_TREE
      .replace(/:forEntry/, forEntry)
      .replace(/:forReclass/, forReclass)
      .replace(/:periodName/, periodName)
      .replace(/:companyCode/, companyCode)
      .replace(/:taxYear/, taxYear)
      .replace(/:countryTaxType/, countryTaxType)
  )
    .then(handleErrors({ errorMessage: 'Error Fetching Country Tax Types' }))
    .then(parseAsJson);

export const fetchDeferredTaxRate = async ({ periodName, companyCode, countryTaxType }) =>
  await fetch(
    Config.endpoints.FIND_DEFERRED_TAX_RATE
      .replace(/:periodName/, periodName)
      .replace(/:companyCode/, companyCode)
      .replace(/:countryTaxType/, countryTaxType)
  )
    .then(handleErrors({ errorMessage: 'Error fetching Deferred Tax Rate' }))
    .then(parseAsJson);

export const fetchObligationRates = async ({ periodName, companyCode, countryTaxType }) =>
  await fetch(
    Config.endpoints.FIND_OBLIGATION_RATES
      .replace(/:periodName/, periodName)
      .replace(/:companyCode/, companyCode)
      .replace(/:countryTaxType/, countryTaxType)
  )
    .then(handleErrors({ errorMessage: 'Error fetching obligation rates' }))
    .then(parseAsJson);

export const fetchTypeDescriptions = async periodName =>
  await fetch(
    Config.endpoints.FETCH_TYPE_DESCRIPTIONS
      .replace(/:periodName/, periodName)
  )
    .then(handleErrors({ errorMessage: 'Error fetching type descriptions.' }))
    .then(parseAsJson);

export const fetchActivityDescriptions = async periodName =>
  await fetch(
    Config.endpoints.FETCH_ACTIVITY_DESCRIPTIONS
      .replace(/:periodName/, periodName)
  )
    .then(handleErrors({ errorMessage: 'Error fetching activity descriptions.' }))
    .then(parseAsJson);

export const fetchTbeErrors = async periodName => await fetch(
  Config.endpoints.FETCH_TAX_ENTRIES_TBE_ERRORS
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Error fetching  TBE Errors.' }))
  .then(parseAsJson);

export const fetchObligations = periodName => fetch(
  Config.endpoints.FETCH_OBLIGATIONS
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching Obligations failed' }))
  .then(parseAsJson);
